/**
 * taskAsk — the ADD affordance of the task queue (feedback/request semantics).
 *
 * A task ask IS a human message announcement on the EXISTING channel command
 * path (announce → outlet enqueueWorld → world push — no new transport): the
 * message artifact carries the ask text as `input.text`, and the payload.ref
 * lane DECLARES the task ask with the oracle task atoms + the target queue
 * lane (sci//@org:task:@task** for the company queue, sci//@sci:task:@task**
 * for the public Causit queue). World owns minting the task row from that
 * declaration; the client only declares and renders.
 *
 * THE ASK RIDES THE ARTIFACT IT WAS RAISED IN (Ori 2026-07-26 — the agent
 * BOOK template is dead; the AGENT ACTOR is the thing, walkable to its living
 * artifact through sci//agent:artifact:@artifact, and an agent may wear any
 * number of UI templates). The ask lands on that artifact's LOGICAL
 * conversation (id = the artifact machine id, claimed world-side at its
 * compose moment). The client NEVER mints a conversation for an ask (the old
 * conv-task-* mint was the fresh-conversation-per-ask bug) and never pins a
 * template id.
 */

export type TaskQueueLane = "org" | "sci";

// The queue membership lanes — oracle atoms (graph.py SciDomain).
export const TASK_QUEUE_LANES: Record<TaskQueueLane, string> = {
  org: "sci//@org:task:@task**",
  sci: "sci//@sci:task:@task**",
} as const;

// The task-ask declaration: the task family atoms the world's task writer
// dispatches on. The ask is a human message (task:kind:@message).
export const TASK_ASK_REFS = [
  "sci//task:@machine:@binary",
  "sci//task:kind:@message",
  "sci//task:status:@status",
] as const;

/** The queue lane a refs lane DECLARES — routing is the declared atom, never
 * a machine-name sniff. */
export const taskQueueLaneOfRefs = (refs: unknown): TaskQueueLane | null => {
  if (!Array.isArray(refs)) return null;
  const spelled = refs.map(String);
  if (spelled.includes(TASK_QUEUE_LANES.org)) return "org";
  if (spelled.includes(TASK_QUEUE_LANES.sci)) return "sci";
  return null;
};

/** The queue lane an artifact DECLARES on its refs. */
export const taskQueueLaneOfArtifact = (artifact: unknown): TaskQueueLane | null =>
  taskQueueLaneOfRefs((artifact as { refs?: unknown[] } | undefined)?.refs);

/** THE ONE ask-routing owner for every interaction face (stream box AND the
 * prioritized rail render the SAME queue artifact): a 'submitted' interaction from
 * an artifact that DECLARES a queue lane on its refs rides the channel
 * command path as a task ask INTO the artifact's own conversation. Returns
 * whether the interaction was a task ask. */
export const routeTaskAskInteraction = (
  announce: (announcement: ReturnType<typeof taskAskAnnounce>) => Promise<unknown>,
  a: { conversationId?: string },
  artifact: unknown,
  interaction: string,
  detail?: Record<string, unknown>,
  /** invoked after the announce SUCCEEDS (the compose-at-write re-download
   * moment — the caller bumps its feed epoch; never a poll). */
  onRouted?: () => void,
): boolean => {
  if (interaction !== "submitted") return false;
  // the FORM may declare its lane word (both queues are offered);
  // the artifact's declared lane atom is the fallback.
  const declaredLane = detail?.lane === "org" || detail?.lane === "sci" ? detail.lane : null;
  const lane = declaredLane ?? taskQueueLaneOfArtifact(artifact);
  if (!lane) return false;
  const query = typeof detail?.query === "string" ? detail.query.trim() : "";
  if (!query) return false;
  // BORN ASSIGNED (agent wave; THE LIST LAW 2026-07-25): the agent
  // artifact's Draft form emits its own agent machine id beside the ask
  // text — forwarded as the declared `body.data.agent` evidence (a LIST —
  // sci//task:@agent:@map** is plural) so the world mints task + assignment
  // edges in the SAME crossing.
  const agent = typeof detail?.agent === "string" ? detail.agent.trim() : "";
  // the ask rides its ORIGIN artifact's own conversation (the artifact's
  // logical conversation — its machine id by construction, never a per-ask
  // mint and never a pinned template id).
  const conversationId = a.conversationId
    || String((artifact as { machine?: { id?: unknown } })?.machine?.id ?? "");
  void announce(taskAskAnnounce(query, lane, { conversationId, ...(agent ? { agents: [agent] } : {}) }))
    .then(() => onRouted?.())
    .catch((error) => console.error("[task-ask] the request announce failed (never reached the world):", error));
  return true;
};

/** THE CARRY LANES (optional, mirrored by the world's queue writer —
 * control/demand.py `queue_task_declaration_rows` reads the DECLARED evidence
 * slots `body.data.{ref,primitive,blob}` onto `body.task.*`): `ref` = what the
 * task is ABOUT (world row addresses the client already holds — a feed row's
 * served `raw.key`, an agent row address); `primitive`/`blob` = carried
 * attachments (an uploaded file's artifact row address). Empty lanes are
 * OMITTED — the world treats absent as none. */
export type TaskAskCarry = {
  ref?: string[];
  primitive?: string[];
  blob?: string[];
};

const CARRY_LANES = ["ref", "primitive", "blob"] as const;

const carrySlotsOf = (carry?: TaskAskCarry): Partial<Record<(typeof CARRY_LANES)[number], string[]>> => {
  const out: Partial<Record<(typeof CARRY_LANES)[number], string[]>> = {};
  for (const lane of CARRY_LANES) {
    const values = (carry?.[lane] || [])
      .map((value) => String(value || "").trim())
      .filter(Boolean);
    if (values.length) out[lane] = values;
  }
  return out;
};

/** BORN ASSIGNED (agent wave; THE LIST LAW): an ask declaring `opts.agents`
 * carries the chosen agent machine ids as the LIST evidence slot
 * `body.data.agent` (sci//task:@agent:@map** is plural — the task HAS an
 * agent list, never an either/or) — the world mints the task row AND its
 * human-confidence assignment edges in the SAME crossing (the ref lane is
 * unchanged). */
export const taskAskAnnounce = (text: string, lane: TaskQueueLane, opts: { conversationId: string; carry?: TaskAskCarry; agents?: string[] }): {
  conversationId: string;
  timestamp: string;
  machine: Record<string, unknown>;
  ref: string[];
  artifact: unknown[];
  evidence: unknown[];
  confirmation: unknown[];
} => {
  const ask = String(text || "").trim();
  if (!ask) throw new Error("a task ask requires text");
  const conversationId = String(opts?.conversationId || "").trim();
  if (!conversationId) {
    // fail loud: an ask without its queue conversation would mint a fresh
    // conversation per ask — the exact bug the artifact conversation killed.
    throw new Error("a task ask rides its origin artifact's own conversation — conversationId is required");
  }
  const id = `task-ask-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
  return {
    conversationId,
    timestamp: new Date().toISOString().replace(/Z$/, ""),
    machine: { name: "message", id, label: "Request", status: "start" },
    ref: [...TASK_ASK_REFS, TASK_QUEUE_LANES[lane]],
    artifact: [{
      machine: { name: "message", id, label: "Request", tags: ["Message", "Request"], status: "start" },
      suffix: "md",
      position: "match",
      input: { text: ask },
      params: [],
    }],
    evidence: [{
      kind: "evidence",
      confidence: 1,
      body: {
        data: {
          text: ask,
          ...carrySlotsOf(opts.carry),
          // data.agent is a LIST — the born-assigned carry (the list law).
          ...(opts.agents?.length ? { agent: opts.agents.map((a) => String(a || "").trim()).filter(Boolean) } : {}),
        },
      },
    }],
    confirmation: [],
  };
};
